import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./VisitResturants.css";
import "./CategoriesCompact.css";
import PromoSection from "./PromoSection";

const CATEGORY_ALIASES = {
  restaurant: "restaurants",
  restaurants: "restaurants",
  dining: "restaurants",

  grocery: "groceries",
  groceries: "groceries",
  "grocery store": "groceries",
  "grocery stores": "groceries",
  supermarket: "groceries",
  supermarkets: "groceries",

  foodtruck: "food trucks",
  "food truck": "food trucks",
  "food trucks": "food trucks",

  realtor: "realtors",
  realtors: "realtors",
  "real estate": "realtors",

  childcare: "child care",
  "childcare services": "child care",
  "child care": "child care",

  it: "it services",
  "it service": "it services",
  "it services": "it services",

  electrician: "electrical",
  electricians: "electrical",

  plumber: "plumbing",
  plumbers: "plumbing",

  attorney: "legal",
  attorneys: "legal",
  lawyer: "legal",
  lawyers: "legal",

  photographer: "photography",
  photographers: "photography",

  transportation: "transportation",
  transport: "transportation",

  other: "other services",
  others: "other services",
  multiple: "multiple services",
};

const CATEGORY_LABELS = {
  accommodation: "Accommodation",
  beauty: "Beauty",
  boutique: "Boutiques",
  catering: "Catering",
  "child care": "Child care",
  construction: "Construction",
  consulting: "Consulting",
  design: "Design",
  education: "Education",
  electrical: "Electrical services",
  "elderly care": "Elderly care",
  employment: "Employment",
  entertainment: "Entertainment",
  "event management": "Event management",
  financial: "Financial services",
  "fitness training": "Fitness training",
  "food trucks": "Food trucks",
  gardening: "Gardening",
  groceries: "Grocery stores",
  health: "Health services",
  "home help": "Home help",
  "it services": "IT services",
  jewelry: "Jewelry",
  kosher: "Kosher businesses",
  legal: "Legal services",
  "legal advice": "Legal advice",
  marketing: "Marketing",
  "multiple services": "Multiple services",
  "other services": "Other services",
  "personal shopping": "Personal shopping",
  "pet care": "Pet care",
  photography: "Photography",
  plumbing: "Plumbing",
  realtors: "Realtors",
  "repair services": "Repair services",
  restaurants: "Restaurants",
  sports: "Sports",
  storage: "Storage",
  "student services": "Student services",
  tailor: "Tailors",
  translation: "Translation",
  transportation: "Transportation",
  tutoring: "Tutoring",
  vegan: "Vegan businesses",
  venues: "Venues",
  "worship places": "Worship places",
};

const CATEGORY_EYEBROWS = {
  restaurants: "Food",
  groceries: "Essentials",
  "food trucks": "Street food",
  catering: "Food services",
  beauty: "Lifestyle",
  boutique: "Shopping",
  gardening: "Home and garden",
  photography: "Creative services",
  realtors: "Real estate",
  "child care": "Family services",
  "elderly care": "Care services",
  electrical: "Home services",
  plumbing: "Home services",
  "it services": "Technology",
  transportation: "Transportation",
  education: "Learning",
  tutoring: "Learning",
  health: "Health and wellness",
  legal: "Professional services",
  consulting: "Professional services",
  construction: "Home services",
  marketing: "Business services",
  jewelry: "Shopping",
  entertainment: "Entertainment",
  venues: "Events",
  "worship places": "Community",
};

const PREFERRED_CATEGORY_ORDER = [
  "restaurants",
  "groceries",
  "food trucks",
];

function normalizeCategoryValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function slugify(value) {
  return normalizeCategoryValue(value)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "other-services";
}

function getBusinessCategory(business) {
  const rawCategory =
    business?.business_type ||
    business?.businessType ||
    business?.category ||
    business?.category_name ||
    business?.categoryName ||
    "other services";

  let normalized = normalizeCategoryValue(rawCategory);

  if (
    ["others", "other", "multiple", "other services", "multiple services"].includes(
      normalized
    )
  ) {
    const specifiedType =
      business?.other_business_type ||
      business?.otherBusinessType ||
      business?.specified_business_type ||
      business?.specifiedBusinessType;

    if (specifiedType) {
      normalized = normalizeCategoryValue(specifiedType);
    }
  }

  return CATEGORY_ALIASES[normalized] || normalized || "other services";
}

function formatCategoryLabel(category) {
  if (CATEGORY_LABELS[category]) {
    return CATEGORY_LABELS[category];
  }

  return String(category || "Other services")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function isPremiumBusiness(item) {
  const plan = normalizeCategoryValue(
    item?.listing_plan || item?.listingPlan || item?.plan || item?.tier
  );

  return (
    plan === "premium" ||
    item?.premium === true ||
    item?.isPremium === true ||
    item?.is_premium === true
  );
}

function getInternalRoute(item) {
  const candidate =
    item?.profile_path ||
    item?.profilePath ||
    item?.internal_route ||
    item?.internalRoute ||
    item?.url ||
    "";

  return String(candidate).startsWith("/") ? String(candidate) : "";
}

function getWebsiteHref(item) {
  const candidates = [
    item?.website,
    item?.website_url,
    item?.websiteUrl,
    item?.external_url,
    item?.externalUrl,
    item?.url,
  ];

  const website = candidates.find((value) => /^https?:\/\//i.test(String(value || "")));
  return website ? String(website) : "";
}

function getMapHref(item) {
  const directMap = item?.mapUrl || item?.map_url || item?.google_maps_url;
  if (directMap) return directMap;

  if (item?.address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      item.address
    )}`;
  }

  return "";
}

function getPhoneHref(phone) {
  const cleaned = String(phone || "").replace(/[^\d+]/g, "");
  return cleaned ? `tel:${cleaned}` : "";
}

function getBusinessIdentity(item) {
  if (item?.id) return `id:${item.id}`;
  if (item?.businessId) return `business:${item.businessId}`;

  return [item?.name, item?.phone, item?.phoneNumber, item?.address]
    .map((value) => normalizeCategoryValue(value))
    .join("|");
}

function collectBusinesses(data = {}) {
  const businessesByKey = new Map();

  const addItems = (items, fallbackCategory = "", forcePremium = false) => {
    if (!Array.isArray(items)) return;

    items.forEach((item) => {
      if (!item || !item.name) return;

      const enriched = {
        ...item,
        category:
          item.category ||
          item.business_type ||
          item.businessType ||
          fallbackCategory ||
          "other services",
      };

      if (forcePremium && !isPremiumBusiness(enriched)) {
        enriched.listing_plan = "premium";
      }

      const key = getBusinessIdentity(enriched);
      const existing = businessesByKey.get(key);

      if (!existing) {
        businessesByKey.set(key, enriched);
        return;
      }

      businessesByKey.set(key, {
        ...existing,
        ...enriched,
        category:
          enriched.category ||
          existing.category ||
          fallbackCategory ||
          "other services",
        listing_plan:
          isPremiumBusiness(existing) || isPremiumBusiness(enriched)
            ? "premium"
            : enriched.listing_plan || existing.listing_plan,
      });
    });
  };

  addItems(data.businesses);
  addItems(data.items);
  addItems(data.directoryItems);

  if (Array.isArray(data.categorySections)) {
    data.categorySections.forEach((section) => {
      addItems(
        section?.items,
        section?.category || section?.title || section?.sectionKey || ""
      );
    });
  }

  // Backward compatibility with the original fixed community-page data shape.
  addItems(data.premiumListings, "", true);
  addItems(data.restaurants, "restaurants");
  addItems(data.groceries, "groceries");
  addItems(data.foodTrucks, "food trucks");
  addItems(data.otherBusinesses, "other services");

  return Array.from(businessesByKey.values());
}

function getDescriptionOverride(sectionDescriptions, category) {
  const aliases = {
    restaurants: ["restaurants"],
    groceries: ["groceries"],
    "food trucks": ["foodTrucks", "food trucks"],
    "other services": ["otherBusinesses", "other services"],
  };

  const possibleKeys = [category, ...(aliases[category] || [])];

  for (const key of possibleKeys) {
    if (sectionDescriptions?.[key]) {
      return sectionDescriptions[key];
    }
  }

  return "";
}

function buildCategorySections(businesses, sectionDescriptions = {}) {
  const groups = new Map();

  businesses.forEach((business) => {
    const category = getBusinessCategory(business);

    if (!groups.has(category)) {
      groups.set(category, []);
    }

    groups.get(category).push(business);
  });

  return Array.from(groups.entries())
    .map(([category, items]) => {
      const title = formatCategoryLabel(category);
      const sortedItems = [...items].sort((first, second) => {
        const firstPremium = isPremiumBusiness(first);
        const secondPremium = isPremiumBusiness(second);

        if (firstPremium !== secondPremium) {
          return firstPremium ? -1 : 1;
        }

        return String(first.name || "").localeCompare(String(second.name || ""));
      });

      return {
        sectionKey: `category-${slugify(category)}`,
        id: `category-${slugify(category)}`,
        category,
        eyebrow: CATEGORY_EYEBROWS[category] || "Business category",
        title,
        description:
          getDescriptionOverride(sectionDescriptions, category) ||
          `Browse ${title.toLowerCase()} serving this community.`,
        items: sortedItems,
      };
    })
    .sort((first, second) => {
      const firstPriority = PREFERRED_CATEGORY_ORDER.indexOf(first.category);
      const secondPriority = PREFERRED_CATEGORY_ORDER.indexOf(second.category);

      if (firstPriority !== -1 || secondPriority !== -1) {
        if (firstPriority === -1) return 1;
        if (secondPriority === -1) return -1;
        return firstPriority - secondPriority;
      }

      return first.title.localeCompare(second.title);
    });
}

function matchesQuery(item, rawQuery) {
  const query = String(rawQuery || "").trim().toLowerCase();
  if (!query) return true;

  const category = formatCategoryLabel(getBusinessCategory(item));
  const specialties = Array.isArray(item?.specialties)
    ? item.specialties.join(" ")
    : "";
  const communities = Array.isArray(item?.communities)
    ? item.communities.join(" ")
    : "";
  const languages = Array.isArray(item?.languages)
    ? item.languages.join(" ")
    : "";

  const haystack = [
    item?.name,
    item?.address,
    item?.phone,
    item?.phoneNumber,
    category,
    specialties,
    communities,
    languages,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(query);
}

function InfoModal({ item, onClose, modalIdPrefix = "community-directory" }) {
  if (!item) return null;

  const internalRoute = getInternalRoute(item);
  const website = getWebsiteHref(item);
  const mapHref = getMapHref(item);
  const phone = item?.phone || item?.phoneNumber || "";
  const phoneHref = getPhoneHref(phone);
  const specialties = Array.isArray(item?.specialties) ? item.specialties : [];

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div
        className="popup-box"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${modalIdPrefix}-modal-title`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="popup-box__head">
          <div>
            <span className="popup-box__eyebrow">Directory details</span>
            <h3 id={`${modalIdPrefix}-modal-title`}>{item.name}</h3>
          </div>

          <button
            type="button"
            className="popup-close"
            onClick={onClose}
            aria-label="Close details"
          >
            ×
          </button>
        </div>

        <div className="popup-box__section">
          <strong>Category</strong>
          <p>{formatCategoryLabel(getBusinessCategory(item))}</p>
        </div>

        {specialties.length > 0 && (
          <div className="popup-box__section">
            <strong>Specialties</strong>
            <p>{specialties.join(" • ")}</p>
          </div>
        )}

        {item.address && (
          <div className="popup-box__section">
            <strong>Address</strong>
            <p>{item.address}</p>
          </div>
        )}

        {phone && (
          <div className="popup-box__section">
            <strong>Phone</strong>
            <p>
              {phoneHref ? <a href={phoneHref}>{phone}</a> : phone}
            </p>
          </div>
        )}

        <div className="popup-box__actions">
          {internalRoute && (
            <Link to={internalRoute} className="visit-card__btn" onClick={onClose}>
              Open business page
            </Link>
          )}

          {website && (
            <a
              href={website}
              target="_blank"
              rel="noopener noreferrer"
              className="visit-card__btn"
            >
              Visit website
            </a>
          )}

          {mapHref && (
            <a
              href={mapHref}
              target="_blank"
              rel="noopener noreferrer"
              className="visit-card__btn visit-card__btn--secondary"
            >
              Take me there
            </a>
          )}
        </div>

        <hr className="popup-divider" />
        <p className="disclaimer-text">
          Directory listing only. Details may come from public sources and should
          be confirmed directly with the business.
        </p>
      </div>
    </div>
  );
}

function CategorySection({
  section,
  searchQuery,
  onTileClick,
  navigate,
}) {
  if (!section) return null;

  return (
    <section id={section.id} className="category-section">
      <div className="category-section__head">
        <div>
          <span className="category-section__eyebrow">{section.eyebrow}</span>
          <div className="category-header-row">
            <h2 className="category-header">{section.title}</h2>
            <span className="category-count">
              {section.items.length}{" "}
              {section.items.length === 1 ? "listing" : "listings"}
            </span>
          </div>
          <p className="category-subtitle">{section.description}</p>
        </div>
      </div>

      <div className="new-tile-container-new">
        {section.items.map((item, index) => {
          const internalRoute = getInternalRoute(item);
          const mapHref = getMapHref(item);
          const phone = item?.phone || item?.phoneNumber || "";
          const phoneHref = getPhoneHref(phone);
          const specialties = Array.isArray(item?.specialties)
            ? item.specialties.slice(0, 3)
            : [];
          const isPremium = isPremiumBusiness(item);
          const cardKey = getBusinessIdentity(item) || `${item.name}-${index}`;

          return (
            <article
              key={`${section.sectionKey}-${cardKey}`}
              className={`new-tile visit-card ${
                isPremium ? "visit-card--premium" : ""
              } ${internalRoute ? "tile-clickable" : "tile-informational"}`}
            >
              {isPremium && (
                <div className="top-ribbon">
                  <span>Premium</span>
                </div>
              )}

              <h3 className="tile-name visit-card__title">
                {item.name}
                {!internalRoute && (
                  <span className="info-badge" title="Open details">
                    ⓘ
                  </span>
                )}
              </h3>

              {specialties.length > 0 && (
                <div className="visit-card__chips">
                  {specialties.map((specialty) => (
                    <span key={specialty} className="visit-chip">
                      {specialty}
                    </span>
                  ))}
                </div>
              )}

              {item.address && <p className="visit-card__address">{item.address}</p>}

              {phone && (
                <p className="visit-card__phone">
                  {phoneHref ? <a href={phoneHref}>{phone}</a> : phone}
                </p>
              )}

              <div className="visit-card__actions">
                {internalRoute ? (
                  <button
                    type="button"
                    className="visit-card__btn"
                    onClick={() => navigate(internalRoute)}
                  >
                    Open page
                  </button>
                ) : (
                  <button
                    type="button"
                    className="visit-card__btn"
                    onClick={() => onTileClick(item)}
                  >
                    View details
                  </button>
                )}

                {mapHref && (
                  <a
                    href={mapHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="visit-card__btn visit-card__btn--secondary"
                  >
                    Map
                  </a>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {section.items.length === 0 && (
        <div className="visit-empty">
          No results matched <strong>{searchQuery}</strong> in this section.
        </div>
      )}
    </section>
  );
}

export default function CommunityDirectoryPage({
  data = {},
  eyebrow,
  title,
  description,
  tags = ["Local-first", "Quick details", "Mobile-friendly"],
  claimText = "Want your business featured here?",
  modalIdPrefix = "community-directory",
  sectionDescriptions = {},
}) {
  const [selected, setSelected] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const allBusinesses = useMemo(() => collectBusinesses(data), [data]);

  const sections = useMemo(
    () => buildCategorySections(allBusinesses, sectionDescriptions),
    [allBusinesses, sectionDescriptions]
  );

  const filteredSections = useMemo(() => {
    const filtered = sections.map((section) => ({
      ...section,
      items: section.items.filter((item) => matchesQuery(item, searchQuery)),
    }));

    if (!searchQuery.trim()) {
      return filtered;
    }

    return filtered.filter((section) => section.items.length > 0);
  }, [sections, searchQuery]);

  const totalResults = filteredSections.reduce(
    (sum, section) => sum + section.items.length,
    0
  );

  const premiumCount = allBusinesses.filter(isPremiumBusiness).length;

  const stats = [
    { label: "Businesses", value: allBusinesses.length },
    { label: "Categories", value: sections.length },
    premiumCount > 0 ? { label: "Premium", value: premiumCount } : null,
  ].filter(Boolean);

  useEffect(() => {
    if (!selected) return undefined;

    const previousOverflow = document.body.style.overflow;
    const onEscape = (event) => {
      if (event.key === "Escape") setSelected(null);
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onEscape);
    };
  }, [selected]);

  return (
    <div className="visit-resturants-page">
      <div className="categories-container">
        <section className="visit-hero">
          <div className="visit-hero__copy">
            <span className="visit-hero__eyebrow">{eyebrow}</span>
            <h1>{title}</h1>
            <p>{description}</p>

            <div className="visit-hero__tags">
              {tags.map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
          </div>

          <div className="visit-hero__stats">
            <div className="visit-stat-grid">
              {stats.map((stat) => (
                <div key={stat.label} className="visit-stat-card">
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <PromoSection />

        <section className="visit-toolbar">
          <div className="visit-toolbar__search">
            <label htmlFor={`${modalIdPrefix}-search`}>Search this guide</label>
            <input
              id={`${modalIdPrefix}-search`}
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search names, categories, specialties, or addresses..."
              className="visit-search-input"
            />
            <p className="visit-toolbar__hint">
              {searchQuery
                ? `${totalResults} result${
                    totalResults === 1 ? "" : "s"
                  } found across this page.`
                : "Search names, categories, specialties, or locations to narrow the listings below."}
            </p>
          </div>

          <div className="visit-jumps">
            {sections.map((section) => (
              <a
                key={section.sectionKey}
                href={`#${section.id}`}
                className="visit-jump-link"
              >
                {section.title}
              </a>
            ))}

            {searchQuery && (
              <button
                type="button"
                className="visit-jump-link visit-jump-link--ghost"
                onClick={() => setSearchQuery("")}
              >
                Clear search
              </button>
            )}
          </div>
        </section>

        {allBusinesses.length === 0 && (
          <div className="visit-empty">
            No businesses are currently listed for this community.
          </div>
        )}

        {allBusinesses.length > 0 && searchQuery && filteredSections.length === 0 && (
          <div className="visit-empty">
            No businesses matched <strong>{searchQuery}</strong>.
          </div>
        )}

        {filteredSections.map((section, index) => (
          <React.Fragment key={section.sectionKey}>
            <CategorySection
              section={section}
              searchQuery={searchQuery}
              onTileClick={setSelected}
              navigate={navigate}
            />

            {index === 0 && (
              <div className="claim-spot-banner">
                {claimText}
                <Link to="/partner-signup#joinForm" className="claim-link">
                  Claim Your Spot
                </Link>
              </div>
            )}
          </React.Fragment>
        ))}

        <div className="back-home-tile">
          <Link to="/hub" className="back-home-link">
            Back to Hub
          </Link>
          <Link to="/" className="back-home-link back-home-link--secondary">
            Homepage
          </Link>
        </div>

        <InfoModal
          item={selected}
          onClose={() => setSelected(null)}
          modalIdPrefix={modalIdPrefix}
        />
      </div>
    </div>
  );
}
